<script lang="ts">
	import { onMount } from 'svelte';
	import Navbar from '../components/Navbar.svelte';
    import { getTokenStorage } from '../utils/localStorage';
	import { getStarredRepositories, type StarredRepository } from '../utils/starred';

	let starredRepositories = $state<StarredRepository[]>([]);
	let loading = $state(true);
	let error = $state('');
    let isAuthenticated = $state(false);

    $effect(() => {
        const token = getTokenStorage();
        isAuthenticated = !!token;
        if (!isAuthenticated) {
             window.location.hash = '/login';
        }
    });

	onMount(async () => {
		await loadStarredRepositories();
	});

	async function loadStarredRepositories() {
		try {
			loading = true;
			error = '';
			starredRepositories = await getStarredRepositories();
			console.log('Loaded starred repositories:', starredRepositories.length);
		} catch (e: any) {
			error = e.message || 'Failed to load starred repositories';
			console.error('Error loading starred repositories:', e);
		} finally {
			loading = false;
		}
	}
</script>

<Navbar />

<div class="min-h-screen bg-gray-50 dark:bg-gray-950">
	<!-- Header -->
	<div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
		<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
			<h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Starred Repositories</h1>
			<p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
				Repositories you have starred.
			</p>
		</div>
	</div>

	<!-- Main Content -->
	<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
		{#if loading}
			<!-- Loading State -->
			<div class="flex items-center justify-center py-12">
				<div class="text-center">
					<div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
					<p class="text-gray-600 dark:text-gray-400">Loading starred repositories...</p>
				</div>
			</div>
		{:else if error}
			<!-- Error State -->
			<div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
				<div class="flex">
					<div class="flex-shrink-0">
						<svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
							<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
						</svg>
					</div>
					<div class="ml-3">
						<h3 class="text-sm font-medium text-red-800 dark:text-red-200">Error loading starred repositories</h3>
						<p class="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
					</div>
				</div>
			</div>
		{:else if starredRepositories.length === 0}
			<!-- Empty State -->
			<div class="text-center py-12">
				<svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
				</svg>
				<h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No starred repositories</h3>
				<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
					You haven't starred any repositories yet. Start exploring and star your favorite projects!
				</p>
				<div class="mt-6">
					<a
						href="#/repositories"
						class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
					>
						<svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
						</svg>
						Explore Repositories
					</a>
				</div>
			</div>
		{:else}
			<!-- Search and Filters -->
			<div class="mb-6 flex flex-col sm:flex-row gap-4">
				<div class="relative flex-1">
					<div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
						<svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
						</svg>
					</div>
					<input
						type="text"
						class="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-gray-100"
						placeholder="Search starred repositories..."
					/>
				</div>
				<div class="flex gap-2">
					<select class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
						<option>All Languages</option>
						<option>JavaScript</option>
						<option>TypeScript</option>
						<option>Go</option>
						<option>Rust</option>
					</select>
					<select class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
						<option>Sort by: Recently starred</option>
						<option>Recently active</option>
						<option>Most stars</option>
					</select>
				</div>
			</div>

			<!-- Repository List -->
			<div class="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md border border-gray-200 dark:border-gray-700">
				<ul class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each starredRepositories as repo}
						<li>
						<a href="#/users/{repo.owner}/repositories/{repo.name}" class="block hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-150 ease-in-out">
							<div class="px-4 py-4 sm:px-6">
								<div class="flex items-center justify-between">
									<div class="flex items-center">
										<p class="text-lg font-semibold text-indigo-600 dark:text-indigo-400 truncate mr-3">
											{repo.owner} / {repo.name}
										</p>
										<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {repo.visibility === 'public' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}">
											{repo.visibility}
										</span>
									</div>
									<div class="ml-2 flex-shrink-0 flex">
										<p class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
											Starred {repo.updatedAt}
										</p>
									</div>
								</div>
								<div class="mt-2 sm:flex sm:justify-between">
									<div class="sm:flex">
										<p class="flex items-center text-sm text-gray-500 dark:text-gray-400 mr-6">
											{repo.description}
										</p>
									</div>
								</div>
								<div class="mt-2 sm:flex sm:justify-between">
									<div class="sm:flex items-center text-sm text-gray-500 dark:text-gray-400">
										<div class="flex items-center mr-6">
											<span class="w-3 h-3 rounded-full mr-1.5" style="background-color: {repo.languageColor}"></span>
											{repo.language}
										</div>
										<div class="flex items-center mr-6">
											<svg class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
											</svg>
											{repo.stars.toLocaleString()}
										</div>
										<div class="flex items-center">
											<svg aria-hidden="true" height="16" viewBox="0 0 16 16" version="1.1" width="16" data-view-component="true" class="octicon octicon-repo-forked mr-2 text-gray-400 fill-current">
												<path d="M5 5.372v.878c0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75v-.878a2.25 2.25 0 1 1 1.5 0v.878a2.25 2.25 0 0 1-2.25 2.25h-1.5v2.128a2.251 2.251 0 1 1-1.5 0V8.5h-1.5A2.25 2.25 0 0 1 3.5 6.25v-.878a2.25 2.25 0 1 1 1.5 0ZM5 3.25a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Zm6.75.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm-3 8.75a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z"></path>
											</svg>
											{repo.forks.toLocaleString()}
										</div>
									</div>
								</div>
							</div>
						</a>
					</li>
					{/each}
				</ul>
			</div>
		{/if}
	</div>
</div>